import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, MapPin, Trash2, AlertCircle } from 'lucide-react';

interface Property {
  id: string;
  name: string;
  address: string;
  owner_name?: string | null;
  owner_email?: string | null;
  owner_phone?: string | null;
  billing_address?: string | null;
  created_at: string;
}

export function PropertyManager() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    billing_address: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('name');

      if (error) throw error;
      setProperties(data || []);
    } catch (err) {
      console.error('Error loading properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const { error } = await supabase
        .from('properties')
        .insert({
          name: formData.name,
          address: formData.address,
          owner_name: formData.owner_name || null,
          owner_email: formData.owner_email || null,
          owner_phone: formData.owner_phone || null,
          billing_address: formData.billing_address || null,
        });

      if (error) throw error;

      setFormData({
        name: '',
        address: '',
        owner_name: '',
        owner_email: '',
        owner_phone: '',
        billing_address: ''
      });
      setShowForm(false);
      loadProperties();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add property');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this property? This will also delete all associated tickets.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
      loadProperties();
    } catch (err) {
      alert('Failed to delete property: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading properties...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Properties</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-colors"
        >
          <Plus size={20} />
          Add Property
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-secondary-50 border border-secondary-200 rounded-lg space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Building A, Main Campus, Oak Street Complex"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Full property address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            />
          </div>

          <div className="border-t border-secondary-300 pt-4 mt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Owner Information (Optional)</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                <input
                  type="text"
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="Property owner's name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Email</label>
                <input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                  placeholder="owner@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Phone</label>
                <input
                  type="tel"
                  value={formData.owner_phone}
                  onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                <input
                  type="text"
                  value={formData.billing_address}
                  onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  placeholder="If different from property address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="px-6 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-colors"
            >
              Add Property
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setError('');
                setFormData({
                  name: '',
                  address: '',
                  owner_name: '',
                  owner_email: '',
                  owner_phone: '',
                  billing_address: ''
                });
              }}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {properties.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <MapPin className="mx-auto mb-3 text-gray-400" size={48} />
          <p>No properties added yet</p>
          <p className="text-sm mt-1">Add a property to start creating maintenance tickets</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {properties.map((property) => (
            <div
              key={property.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-secondary-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <MapPin className="text-secondary-600 flex-shrink-0 mt-1" size={20} />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-800 truncate">{property.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{property.address}</p>

                    {(property.owner_name || property.owner_email || property.owner_phone) && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-500 mb-1">Owner Information</p>
                        {property.owner_name && (
                          <p className="text-sm text-gray-700">{property.owner_name}</p>
                        )}
                        {property.owner_email && (
                          <p className="text-sm text-gray-600">{property.owner_email}</p>
                        )}
                        {property.owner_phone && (
                          <p className="text-sm text-gray-600">{property.owner_phone}</p>
                        )}
                      </div>
                    )}

                    {property.billing_address && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-500">Billing Address</p>
                        <p className="text-sm text-gray-600">{property.billing_address}</p>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(property.id)}
                  className="text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                  title="Delete property"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
